import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User as UserIcon, Bookmark, Clock, Users, Bell, LogOut, ChevronDown } from 'lucide-react';
import { useUserAuth } from '../contexts/UserAuthContext';
import { resolveImageUrl } from './ImageUpload';

/**
 * Navbar Login button → Profile dropdown when signed in.
 * Replaces the old "Apply Now" CTA on the Dreams League nav.
 */
const NavbarAuthControl = ({ isDark = false }) => {
  const { user, setLoginOpen, logout } = useUserAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!user) {
    return (
      <button
        type="button"
        onClick={() => setLoginOpen(true)}
        className={`btn-pill text-sm lg:text-base whitespace-nowrap ${
          isDark ? 'bg-white text-black ring-2 ring-yellow-500/40' : 'bg-black text-white'
        }`}
        data-testid="nav-login-btn"
      >
        Login
      </button>
    );
  }

  const initials = (user.name || user.email || '?').trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const items = [
    { icon: UserIcon, label: 'My Access', to: '/profile', testid: 'menu-account' },
    { icon: Bookmark, label: 'Saved Stories', to: '/profile?tab=stories', testid: 'menu-saved' },
    { icon: Clock, label: 'Watch History', to: '/profile?tab=history', testid: 'menu-history' },
    { icon: Users, label: 'Following', to: '/profile?tab=following', testid: 'menu-following' },
    { icon: Bell, label: 'Notifications', to: '/profile?tab=notifications', testid: 'menu-notifications' },
  ];

  return (
    <div className="relative" ref={ref}>
      {/* Two-target pill: tapping the avatar goes straight to /profile,
          tapping the chevron opens the dropdown. Same visual pill as
          before — kept the ring + hover styling wrapping both. */}
      <div
        className={`inline-flex items-center gap-2 px-2 py-1 rounded-full ring-1 transition ${
          isDark
            ? 'ring-white/20 hover:bg-white/5'
            : 'ring-black/10 hover:bg-black/5'
        }`}
      >
        <Link
          to="/profile"
          onClick={() => setOpen(false)}
          className="active:scale-95 transition-transform inline-flex items-center rounded-full focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:outline-none"
          aria-label="Go to my profile"
          data-testid="nav-profile-avatar"
        >
          {user.picture ? (
            <img loading="lazy" decoding="async" src={resolveImageUrl(user.picture, 80)} alt="" className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <span className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-[11px] font-bold">{initials}</span>
          )}
        </Link>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="active:scale-95 transition-transform rounded-full p-0.5 focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:outline-none"
          aria-label="Open profile menu"
          aria-expanded={open}
          data-testid="nav-profile-btn"
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''} ${isDark ? 'text-white' : 'text-black'}`} />
        </button>
      </div>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 bg-black text-white rounded-2xl ring-1 ring-white/10 shadow-2xl overflow-hidden z-[90]"
          style={{ animation: 'fadeSlide 200ms cubic-bezier(0.22,1,0.36,1)' }}
          data-testid="nav-profile-dropdown"
        >
          <div className="px-4 py-3 border-b border-white/10">
            <p className="font-bold text-sm truncate">{user.name || 'Member'}</p>
            <p className="text-xs text-white/50 truncate">{user.email}</p>
          </div>
          <div className="py-1">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <Link
                  key={it.testid}
                  to={it.to}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-white/5 transition"
                  data-testid={it.testid}
                >
                  <Icon className="w-4 h-4 text-white/60" />
                  {it.label}
                </Link>
              );
            })}
          </div>
          <div className="border-t border-white/10 py-1">
            <button
              type="button"
              onClick={() => { setOpen(false); logout(); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-300 hover:bg-red-500/10 transition"
              data-testid="menu-logout"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavbarAuthControl;
